// Вкладка «Расчётный лист». Ответ на вопрос «почему так мало» без участия финансового
// директора: часы против нормы, за что начислено, каждое удержание, итог.
//
// Три решения, которые видно на экране:
//
// **Юрлица не складываются.** Если человек прошёл по двум юридическим лицам, показываются два
// раздела и объяснение словами вместо общей суммы: платят по каждому отдельно и в свои дни.
//
// **Ноль по каскаду отличается от нуля по расчёту.** Обнулённый каскадом итог подписан
// причиной: «не хватило начисленного», а не просто ноль — иначе это читается как «ничего не
// положено».
//
// **Долги показываются с пояснением.** Цена билета без объяснения читается как произвол, и
// человек идёт спрашивать — то есть ровно туда, откуда лист должен был его увести.

import { useState } from 'react';

import { Link } from 'react-router';

import { RiCalculatorLine } from '@remixicon/react';

import type { EmployeeOut } from '@/shared/api/endpoints/people';
import { useAuthStore } from '@/shared/auth/store';
import { адресРасчётаСотрудника } from '@/shared/payroll/расчёт-сотрудника';
import { can } from '@/shared/rbac/permissions';
import type {
  PayslipDebt,
  PayslipDeduction,
  PayslipOut,
  PayslipPart,
} from '@/shared/api/endpoints/payslip';
import { formatRubles } from '@/shared/money/format';
import { formatDateOnly } from '@/shared/time/format';
import { formatHours } from '@/shared/hours/format';
import * as Alert from '@/shared/ui/alert';
import * as Badge from '@/shared/ui/badge';
import * as LinkButton from '@/shared/ui/link-button';
import { FieldList, FieldRow } from '@/shared/ui/field-row';
import { PeriodStepper } from '@/shared/ui/period-stepper';
import type { Period } from '@/shared/time/period';
import { formatPeriodTitle, расчётныйМесяц, shiftPeriod } from '@/shared/time/period';
import { exportPayslip } from '@/shared/api/endpoints/payslip';
import { ExportButton } from '@/shared/reports/ExportButton';
import { PrintButton } from '@/shared/reports/PrintButton';
import { EmptyState, ScreenState, Skeleton } from '@/shared/ui/screen-state';

import { usePayslipQuery } from '../queries';

const МЕСЯЦЫ = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];

/**
 * Что осталось по долгу того же вида после этого удержания.
 *
 * Тз, раздел 5.7, требует у каждого удержания остаток долга после него. Без остатка строка
 * отвечает только на «сколько сняли», а человек спрашивает «сколько ещё снимут» — и идёт
 * спрашивать это в финотдел, то есть ровно туда, откуда лист должен был его увести.
 *
 * Связь идёт ПО ВИДУ: каждый тип долга удерживается в одноимённую колонку (тз, раздел 5.5).
 * Долгов одного вида бывает несколько — тогда остатки складываются: человеку важно «сколько
 * ещё за форму», а не по какой из двух записей. Основание удержания не теряется.
 */
function остатокПослеУдержания(
  удержание: PayslipDeduction,
  долги: readonly PayslipDebt[],
): string | undefined {
  const свои = долги.filter((долг) => долг.debt_type === удержание.column_code);
  if (свои.length === 0) {
    return undefined;
  }
  const остаток = свои.reduce((сумма, долг) => сумма + долг.outstanding, 0);
  const хвост =
    удержание.basis === null || удержание.basis === undefined ? '' : ` · ${удержание.basis}`;
  return остаток === 0 ? `долг закрыт${хвост}` : `осталось ${formatRubles(остаток)}${хвост}`;
}

// Названий удержаний и видов долга здесь НЕТ намеренно: они приходят с сервера полями
// `column_title` и `debt_type_title`. Словарь живёт в контракте, и копия его на клиенте
// разъезжается молча: заведут в справочнике новый вид удержания — и человек увидит в
// своём расчётном листе «uniform» вместо «Форма».

function РазделРасчёта({
  часть,
  долги,
}: {
  часть: PayslipPart;
  /** Долги человека: из них берётся остаток, который остаётся после удержания. */
  долги: readonly PayslipDebt[];
}) {
  // Поле необязательное: старый ответ сервера его не содержит, и тогда часы считаем
  // проставленными — так вело себя всё до 25.08.2026.
  const часовНет = часть.hours_filled === false;
  // Почему денег нет, со слов сервера. Отдельно от «часов нет»: у человека без тарифа часы в
  // листе стоят (24 из 744), а начислить не из чего — и до 23.09.2026 лист печатал ему
  // «Начислено 0 ₽ · К выплате 0 ₽». Лист отдают на руки, и ноль там читается как «тебе ничего».
  const неПосчитано =
    часть.not_counted ?? (часть.calculated === false ? 'расчёт ещё не проводился' : null);
  const суммНет = часовНет || неПосчитано !== null;
  return (
    <section className='rounded-xl border border-stroke-soft-200 bg-bg-white-0 p-4'>
      <div className='mb-3 flex flex-wrap items-center gap-2'>
        <span className='text-label-sm text-text-strong-950'>{часть.legal_entity_name}</span>
        {часть.site_name === null || часть.site_name === undefined ? null : (
          <Badge.Root variant='lighter' color='gray' size='medium'>
            {часть.site_name}
          </Badge.Root>
        )}
        {часть.number ? (
          <span className='text-paragraph-xs text-text-sub-600'>№ {часть.number}</span>
        ) : null}
      </div>

      {/* Часы не проставлены — это НЕ ноль часов, и лист обязан их различать.
          «0 из 558» человек читает как «я не выходил и мне ничего не начислено», а правда —
          «табель за месяц ещё не закрыли». По этому листу он решает, идти ли в финотдел, и
          обе дороги неверны: одна — жаловаться на несуществующую недоплату, другая — молча
          согласиться с нулём. Причина называется ОДИН раз, над величинами, а сами величины
          показывают прочерк. */}
      {часовНет && неПосчитано === null && (
        <Alert.Root variant='lighter' status='information' size='small' className='mb-3'>
          <Alert.Icon />
          Часы за этот месяц ещё не проставлены, поэтому расчёт пока пуст. Табель закрывает
          табельщик объекта — после этого суммы появятся здесь сами.
        </Alert.Root>
      )}
      {/* «Появятся сами» обещается ТОЛЬКО там, где не хватает одних часов. У человека без
          тарифа закрытие табеля не даст ничего, и обещание было бы неправдой. */}
      {неПосчитано !== null && (
        <Alert.Root variant='lighter' status='warning' size='small' className='mb-3'>
          <Alert.Icon />
          Начисление за этот месяц не посчитано: {неПосчитано}. Суммы ниже появятся, когда причину
          устранят, — ноля здесь нет, есть «пока неизвестно».
        </Alert.Root>
      )}

      <FieldList>
        <FieldRow
          label='Часы'
          value={
            часовНет
              ? null
              : часть.hours_norm
                ? `${formatHours(часть.hours_fact)} из ${formatHours(часть.hours_norm)}`
                : formatHours(часть.hours_fact)
          }
          missing={часовНет ? 'табель за месяц не закрыт' : undefined}
          note={!часовНет && часть.hours_norm ? 'факт против нормы объекта' : undefined}
        />
        <FieldRow
          label='Начислено'
          value={суммНет ? null : `${formatRubles(часть.accruals_total)}`}
          // Причина названа ОДИН раз, в плашке над величинами. Здесь стояла та же фраза слово в
          // слово («часы за месяц ещё не проставлены: табель объекта не закрыт» дважды в одном
          // разделе, замер независимой проверки 01.10.2026): человек читает вторую копию как
          // второе обстоятельство и ищет, чем они различаются. В строке остаётся состояние.
          missing={
            суммНет ? (неПосчитано !== null ? 'не посчитано' : 'считается от часов') : undefined
          }
        />
        {часть.official_total > 0 ? (
          <FieldRow
            // ЗНАК МИНУС ОБЯЗАТЕЛЕН, и это не оформление. Лист читается как формула сверху
            // вниз: начислено, вычеты, итог. Аванс и удержания стояли с минусом, а официальная
            // часть — без него, хотя вычитается ровно так же. У Шатуна из июньской ведомости
            // это выглядело так: 240 000 начислено, 62 764 официально, минусы на 82 000 — и
            // 125 236 на руки. Сложив то, что помечено минусом, человек получает 158 000 и
            // видит, что «программа посчитала неверно» на 62 764 рубля. Дальше он не верит
            // ни одному числу на экране, и это ровно тот разговор, ради устранения которого
            // лист и сделан.
            //
            // Подпись — от человека, а не от бухгалтерии. «Официальная часть» — наше слово:
            // охранник знает, что часть денег приходит на карту официально, а часть на руки,
            // и спрашивает он «почему на руки меньше». Ответ стоит прямо в строке.
            label='Выплачивается официально'
            value={`− ${formatRubles(часть.official_total)}`}
            note={
              часть.official_paid === undefined
                ? 'идёт по официальной ведомости, а не на руки'
                : `${formatRubles(часть.official_paid)} уже выплачено, ${formatRubles(
                    часть.official_rest ?? 0,
                  )} остаток`
            }
          />
        ) : null}
        {часть.advance_total > 0 ? (
          <FieldRow label='Аванс' value={`− ${formatRubles(часть.advance_total)}`} />
        ) : null}
        {часть.deductions.map((удержание) => (
          <FieldRow
            key={удержание.column_code}
            label={удержание.column_title || удержание.column_code}
            value={`− ${formatRubles(удержание.amount)}`}
            // Тз, раздел 5.7: удержание отдельной строкой С ОСТАТКОМ ДОЛГА после него. Без
            // остатка строка отвечает только на «сколько сняли», а человек спрашивает
            // «сколько ещё снимут» — и идёт спрашивать это в финотдел, то есть ровно туда,
            // откуда лист должен был его увести.
            note={остатокПослеУдержания(удержание, долги) ?? удержание.basis ?? undefined}
          />
        ))}
        <FieldRow
          label='К выплате'
          value={суммНет ? null : `${formatRubles(часть.total_payout)}`}
          missing={
            суммНет
              ? неПосчитано !== null
                ? 'появится, когда начисление посчитают'
                : 'появится после закрытия табеля'
              : undefined
          }
          note={
            часть.zeroed_by_cascade
              ? 'не хватило начисленного: остаток аванса перенесён на следующий месяц'
              : undefined
          }
        />
        {typeof часть.unexplained !== 'number' || часть.unexplained === 0 ? null : (
          // Человек складывает строки и получает другое число — и перестаёт верить всему
          // листу. Разница называется прямо, а не прячется: молчание тут дороже неловкости.
          <FieldRow
            label='Не объяснено строками'
            value={`${formatRubles(часть.unexplained)}`}
            note='расхождение итога с показанными слагаемыми — спросите финансовый отдел'
          />
        )}
      </FieldList>
    </section>
  );
}

function Долги({ долги }: { долги: PayslipDebt[] }) {
  if (долги.length === 0) {
    return null;
  }
  return (
    <section className='flex flex-col gap-2'>
      <span className='text-label-sm text-text-strong-950'>Сколько ещё должен</span>
      <ul className='flex flex-col gap-2'>
        {долги.map((долг) => (
          <li
            key={`${долг.debt_type}-${долг.initial_amount}`}
            className='rounded-xl border border-stroke-soft-200 bg-bg-white-0 px-3 py-2'
          >
            <div className='flex items-baseline justify-between gap-3'>
              <span className='text-paragraph-sm text-text-strong-950'>
                {долг.debt_type_title || долг.debt_type}
              </span>
              <span className='tabular-nums text-label-sm text-text-strong-950'>
                {formatRubles(долг.outstanding)}
              </span>
            </div>
            {долг.note ? (
              <p className='mt-1 text-paragraph-xs text-text-sub-600'>{долг.note}</p>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Лист, которого нет: показывается как пустое состояние, а не как отказ сервера. */
const ПУСТОЙ_ЛИСТ: PayslipOut = {
  employee_id: '',
  employee_name: '',
  period_year: 0,
  period_month: 0,
  parts: [],
  debts: [],
  advances: [],
};

export function PayslipTab({ employee }: { employee: EmployeeOut }) {
  // Период выбирается ТЕМ ЖЕ календарём, что и везде в продукте. Два раздельных списка
  // «Месяц» и «Год» были пятым способом выбрать месяц: чтобы посмотреть предыдущий, человек
  // раскрывал список из двенадцати пунктов и искал в нём нужный — вместо одного нажатия на
  // стрелку. А на границе года ему пришлось бы менять оба списка, и порядок правок при этом
  // ещё и имеет значение.
  //
  // Открывается лист на ПРОШЛОМ месяце, а не на текущем: ведомость за месяц считают после его
  // окончания, и за текущий расчёта не бывает никогда. До 23.09.2026 лист у каждого человека
  // открывался на «За этот месяц расчёта нет» — первым финансовый директор видел пустоту, а
  // нужный месяц стоял в одном нажатии стрелки.
  const [период, setПериод] = useState<Period>(() => расчётныйМесяц(new Date()));
  const год = String(период.year);
  const месяц = String(период.month);
  const query = usePayslipQuery(employee.id, период.year, период.month);
  // Расчёт за все месяцы и объекты сразу: у кого есть право на ведомости. Пустой месяц иначе
  // оставляет листать стрелку назад по одному (живой проход по боевым данным 01.10.2026: лист
  // человека из майской книги открывался на сентябре, и до мая было четыре нажатия).
  const можноРасчёт = can(
    useAuthStore((состояние) => состояние.permissions),
    'core.payroll.view',
  );

  const выборПериода = (
    <PeriodStepper
      label='Месяц расчёта'
      title={formatPeriodTitle(период)}
      onPrev={() => setПериод(shiftPeriod(период, -1))}
      onNext={() => setПериод(shiftPeriod(период, 1))}
    />
  );

  // «За этот месяц расчёта нет» — это не ошибка загрузки, а состояние: ведомость ещё не
  // создана или человек в неё не попал. Показывать его красной плашкой значит пугать там, где
  // ничего не сломалось, — и приучать не читать настоящие ошибки (правило 7).
  const расчётаНет = query.error?.code === 'not_found';

  return (
    // Документ помечен: печать разворачивает его целиком, а не печатает видимый кусок.
    <div data-печать='документ' className='flex flex-col gap-4 p-5'>
      <div data-печать='скрыть' className='flex flex-wrap items-center justify-between gap-2'>
        {выборПериода}
        {/* Оба действия — одной группой у правого края: врозь при justify-between выгрузка
            повисала посреди шапки, и две кнопки одного назначения читались как разные вещи. */}
        {расчётаНет ? null : (
          <div className='flex items-center gap-2'>
            {/* Лист печатают и отдают человеку на руки: это главный документ, которым заказчик
                меряет работу системы. Файл собирает сервер той же выборкой, что и экран. */}
            <ExportButton
              // Слева на том же экране стоит выгрузка реестра, поэтому подпись уточняет, что
              // именно уедет в файл.
              что='расчётный лист'
              // Свой лист — собственные деньги человека: сервер права на отчёты по компании
              // здесь не спрашивает, и кнопка не должна отказывать там, где сервер отдаёт.
              требуетПраво={false}
              собрать={() =>
                exportPayslip(employee.id, {
                  year: Number(год),
                  month: Number(месяц),
                })
              }
            />
            {/* Печать с экрана, а не только файлом. Тз обещает по расчётному листу «доступны
                печать и выгрузка» (сценарий 64), и это единственная страница продукта, которую
                печатают именно с экрана: она одностраничная и её отдают человеку на руки. Всё
                лишнее — полоса разделов, панели, эта самая строка — на бумагу не идёт. */}
            <PrintButton />
          </div>
        )}
      </div>
      <ScreenState
        data={расчётаНет ? ПУСТОЙ_ЛИСТ : query.data}
        error={расчётаНет ? null : (query.error ?? null)}
        subject='расчётный лист'
        isEmpty={(лист) => лист.parts.length === 0}
        onRetry={() => void query.refetch()}
        loading={<Skeleton className='h-40 w-full' />}
        empty={
          <EmptyState
            icon={RiCalculatorLine}
            title='За этот месяц расчёта нет'
            illustration='период'
            description={
              можноРасчёт
                ? 'Ведомость по объекту ещё не создана или человек в неё не попал. Выберите другой месяц или откройте расчёт за все месяцы сразу.'
                : 'Ведомость по объекту ещё не создана или человек в неё не попал. Выберите другой месяц.'
            }
            action={
              можноРасчёт ? (
                <LinkButton.Root variant='primary' size='medium' underline asChild>
                  <Link to={адресРасчётаСотрудника(employee.id)} className='цель-тача relative'>
                    Расчёт за все месяцы
                  </Link>
                </LinkButton.Root>
              ) : undefined
            }
          />
        }
      >
        {(лист) => (
          <div className='flex flex-col gap-4'>
            {/* Лист называет себя САМ: чей он и за какой месяц.

                До 03.09.2026 имя человека попадало на бумагу из шапки карточки — то есть из
                экрана, а не из документа. Печать с этого дня выносит на лист только сам
                документ (`globals.css`), и лист без имени превратился бы в анонимную таблицу
                сумм. Документ, который нельзя опознать, отдавать человеку на руки нельзя. */}
            <header className='flex flex-wrap items-baseline justify-between gap-2 border-b border-stroke-soft-200 pb-2'>
              <span className='text-label-md text-text-strong-950'>
                Расчётный лист · {лист.employee_name}
              </span>
              <span className='text-paragraph-sm text-text-sub-600'>
                {formatPeriodTitle(период)}
              </span>
            </header>
            {лист.total_note ? (
              <p className='rounded-lg bg-bg-weak-50 px-3 py-2 text-paragraph-sm text-text-sub-600'>
                {лист.total_note}
              </p>
            ) : null}
            {лист.parts.map((часть) => (
              <РазделРасчёта key={часть.payroll_employee_id} часть={часть} долги={лист.debts} />
            ))}
            <Долги долги={лист.debts} />
            {лист.advances.length === 0 ? null : (
              <section className='flex flex-col gap-2'>
                <span className='text-label-sm text-text-strong-950'>
                  Авансы за всё время работы
                </span>
                <ul className='flex flex-col gap-1'>
                  {лист.advances.map((аванс) => (
                    <li
                      key={`${аванс.period_year}-${аванс.period_month}-${аванс.legal_entity_name}`}
                      className='flex flex-col gap-0.5 text-paragraph-sm text-text-sub-600'
                    >
                      <span className='flex items-baseline justify-between gap-3'>
                        <span>
                          {МЕСЯЦЫ[аванс.period_month - 1]} {аванс.period_year}
                        </span>
                        <span className='text-text-strong-950'>{formatRubles(аванс.amount)}</span>
                      </span>
                      {/* Даты выдач под месяцем: заказчик просил историю «с указанием дат и
                          сумм» дословно, а месяц на вопрос «когда я это брал» не отвечает.
                          Пусто бывает у перенесённых из Excel расчётов — там выдач как
                          записей нет, и придумывать им даты нельзя. */}
                      {аванс.payouts.length > 0 && (
                        <span className='flex flex-col gap-0.5 pl-3 text-paragraph-xs'>
                          {аванс.payouts.map((выдача, номер) => (
                            <span
                              key={`${выдача.happened_on}-${номер}`}
                              className='flex items-baseline justify-between gap-3'
                            >
                              <span>{formatDateOnly(выдача.happened_on)}</span>
                              <span>{formatRubles(выдача.amount)}</span>
                            </span>
                          ))}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </ScreenState>
    </div>
  );
}
